import React from 'react';
import { useTranslation } from 'next-i18next';

import type { GroupChannel } from '@sendbird/chat/groupChannel';

import type { Medium } from 'src/types/__generated__/graphql';
import { GROUP_CONVERSATION_CUSTOM_TYPE, type SendbirdMember } from 'src/utils/sendbirdUtils';
import useUser from 'src/hooks/useUser';
import useActivityToDisplay from 'src/hooks/useActivityToDisplay';

import GroupConversationAvatar from 'src/components/chat/GroupConversationAvatar';
import SimpleHorizontalCard from 'src/components/ui/SimpleHorizontalCard';
import Avatar from 'src/components/ui/Avatar';

interface Props {
    groupChannel: GroupChannel;
    firstSubtitle?: string | null;
    rightArea?: React.ReactElement;
    padding?: string;
    minHeight?: string;
    onClick: () => void;
    dataTargetId?: string;
    backgroundColor?: string;
}

const SimpleHorizontalGroupChannelCard: React.FC<Props> = ({
    groupChannel,
    firstSubtitle = 'activity',
    rightArea,
    padding = '1rem',
    minHeight,
    onClick,
    dataTargetId,
    backgroundColor,
}) => {
    const { t } = useTranslation(['contacts']);
    const currentUser = useUser();
    const getActivityToDisplay = useActivityToDisplay();

    let subtitle;
    let avatar;
    let name;
    if (groupChannel.customType === GROUP_CONVERSATION_CUSTOM_TYPE) {
        subtitle = t('contacts:groupConversation');
        avatar = <GroupConversationAvatar dataTargetId="channel-card-avatar" channel={groupChannel} size={40} />;
        name = groupChannel.name;
    } else {
        const chatWith: SendbirdMember | undefined = groupChannel.members.find(
            (m) => m.userId !== currentUser?.sendbirdUserId,
        );
        subtitle =
            firstSubtitle === 'activity'
                ? getActivityToDisplay({
                      activity: { labelKey: chatWith?.metaData.activityLabelKey ?? '' },
                  })
                : firstSubtitle;
        avatar = (
            <Avatar
                image={chatWith?.profileUrl ? ({ url: chatWith.profileUrl } as Medium) : undefined}
                alt={chatWith?.nickname}
                fallbackText={chatWith?.metaData.initials}
                size={40}
            />
        );
        name = chatWith?.nickname ?? '';
    }
    return (
        <SimpleHorizontalCard
            keyIdentifier={groupChannel.url}
            firstSubtitle={subtitle}
            rightArea={rightArea}
            padding={padding}
            minHeight={minHeight}
            onClick={onClick}
            dataTargetId={dataTargetId}
            backgroundColor={backgroundColor}
            avatar={avatar}
            name={name}
        />
    );
};

export default SimpleHorizontalGroupChannelCard;
